import { ColumnsType } from "antd/es/table";
import {
  CHART_VALUE_COLOR,
  DATE_FORMAT,
  DISBURSEMENTS_METHOD_TYPES_HISTORY,
  PAYEE_TYPES,
} from "../../../../../../../lib/constants";
import dayjs from "../../../../../../../configs/dayjs";
import { getFormattedCurrency } from "../../../../../../../lib/currencyFormatter";
import { useGetPayeeIcon } from "../../Details/components/PayeeDisbursements/hooks/useGetPayeeIcon";
import { useGetMethodColumnDescription } from "../../Details/components/PayeeDisbursements/hooks/useGetMethodColumnDescription";
import { useMemo } from "react";
import { INestedDisbursements } from "../../../../../../../types/PaymentHistory";
import { Row } from "antd";
import { useGetRole } from "../../../../../../../lib/hooks/getRole";

export const useGetNestedTableColumns = () => {
  const { getIcon } = useGetPayeeIcon(CHART_VALUE_COLOR);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, isAdmin] = useGetRole();

  const { getLine } = useGetMethodColumnDescription();

  const columns: ColumnsType<INestedDisbursements> = useMemo(
    () => [
      {
        title: "Disbursement Date",
        dataIndex: "date",
        key: "date",
        width: 178,
        render: (value: string | Date) => dayjs(value).format(DATE_FORMAT),
      },
      {
        title: "Amount",
        dataIndex: "amount",
        key: "amount",
        width: 140,
        align: "right",
        render: (value: number, record: INestedDisbursements) => {
          const { aggregated, total } = record;

          return (
            <Row className="flex-column">
              {getFormattedCurrency(value)}
              {aggregated && total && (
                <span className="text-sm title">
                  Total: {getFormattedCurrency(total)}
                </span>
              )}
            </Row>
          );
        },
      },
      {
        title: "Payee",
        dataIndex: "payeeName",
        key: "payeeName",
        width: 160,
        ellipsis: true,
        render: (value: string, record: INestedDisbursements) => {
          const { type } = record;

          const payee = PAYEE_TYPES.find(
            (payeeType) => payeeType.value === type,
          );

          const icon = getIcon(payee);

          return (
              <Row className="align-center" wrap={false}>
              {icon}
              <span className="ml-3 elipsis">{value}</span>
            </Row>
          );
        },
      },
      {
        title: "Type",
        dataIndex: "type",
        key: "type",
        width: 160,
        render: (value: number) =>
          PAYEE_TYPES.find((type) => type.value === value)?.label,
      },
      {
        title: "Method",
        dataIndex: "method",
        key: "method",
        width: 188,
        render: (value: number, record: INestedDisbursements) => {
          const { bankAccountNumber, accountNumber, checkNumber, aggregated } =
            record;

          const method = DISBURSEMENTS_METHOD_TYPES_HISTORY.find(
            (method) => method.value === value,
          )?.label;

          const descriptionLine = getLine({
            methodType: value,
            bankAccountNumber,
            accountNumber,
            checkNumber,
          });

          return (
            <Row className="flex-column">
              {aggregated ? "+" : ""}
              {method}
              {(isAdmin || value === 1) && (
                <span className="text-sm title">{descriptionLine}</span>
              )}
            </Row>
          );
        },
      },
      {
        title: "Description",
        dataIndex: "description",
        key: "description",
      },
    ],
    [getIcon, getLine, isAdmin],
  );

  return { columns };
};
